import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  Avatar,
} from '@mui/material';
import {
  Download,
  AccessTime,
  Visibility,
  HolidayVillage,
  CheckCircle,
  Cancel,
  EventBusy,
  Schedule,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { reportsAPI, settingsAPI } from '../services/api';

const EmployeeReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportData, setReportData] = useState(null);
  const [selectedAttendance, setSelectedAttendance] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [weekendOff, setWeekendOff] = useState(true);
  const [firstSaturdayWork, setFirstSaturdayWork] = useState(false);

  const loadWeekendSettings = async () => {
    try {
      const [weekendRes, satWorkRes] = await Promise.all([
        settingsAPI.getByKey('weekend_off'),
        settingsAPI.getByKey('first_saturday_work')
      ]);

      if (weekendRes.data && weekendRes.data.success && weekendRes.data.data) {
        setWeekendOff(weekendRes.data.data.value === 'true');
      }
      if (satWorkRes.data && satWorkRes.data.success && satWorkRes.data.data) {
        setFirstSaturdayWork(satWorkRes.data.data.value === 'true');
      }
    } catch (error) {
      console.log('Weekend settings not loaded, using defaults');
    }
  };

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getMonthly(selectedYear, selectedMonth, null);
      
      if (response.data && response.data.success) {
        setReportData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    loadWeekendSettings();
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const params = {
        reportType: 'monthly',
        employeeId: null,
        month: selectedMonth,
        year: selectedYear,
      };
      
      const response = await reportsAPI.exportReport(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'my-attendance-report.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return new Date(time).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: true 
    });
  };

  const formatMinutes = (minutes) => {
    if (!minutes || minutes === 0) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const isWeekendDay = (date) => {
    const dayOfWeek = date.getDay();
    if (!weekendOff) return false;
    
    if (dayOfWeek === 0) return true;
    
    if (dayOfWeek === 6) {
      if (!firstSaturdayWork) return true;
      
      const day = date.getDate();
      if (day > 7) return true;
      
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const firstDayOfWeek = firstDay.getDay();
      const daysToFirstSaturday = (6 - firstDayOfWeek) % 7;
      const firstSaturdayDate = daysToFirstSaturday + 1;
      
      return day !== firstSaturdayDate;
    }
    
    return false;
  };

  const shouldShowDay = (dateStr) => {
    const date = new Date(dateStr);
    return !isWeekendDay(date);
  };

  const getStatusDisplay = (attendance, leave, isHoliday) => {
    if (isHoliday) {
      return { label: 'Holiday', color: '#CE93D8', icon: <HolidayVillage /> };
    }
    
    if (attendance?.login_time) {
      if (attendance.status === 'late') {
        return { label: 'Late', color: '#FFB74D', icon: <Schedule /> };
      }
      return { label: 'Present', color: '#81C784', icon: <CheckCircle /> };
    }
    
    if (leave) {
      return { label: 'Leave', color: '#90CAF9', icon: <EventBusy /> };
    }
    
    return { label: 'Absent', color: '#EF9A9A', icon: <Cancel /> };
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: '#D4AF37' }}>
          My Attendance Report
        </Typography>
        
        <Card sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#B4C4A8' }}>Year</InputLabel>
                  <Select
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(e.target.value)}
                    sx={{ color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } }}
                  >
                    {[2023, 2024, 2025, 2026].map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#B4C4A8' }}>Month</InputLabel>
                  <Select
                    value={selectedMonth}
                    label="Month"
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    sx={{ color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } }}
                  >
                    {monthNames.map((month, index) => (
                      <MenuItem key={index} value={index}>{month}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <Download />}
                  fullWidth
                  onClick={handleExport}
                  disabled={exporting}
                  sx={{
                    bgcolor: '#D4AF37',
                    color: 'rgb(10, 25, 41)',
                    '&:hover': { bgcolor: '#C19B2E' }
                  }}
                >
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#D4AF37' }} />
          </Box>
        ) : !reportData || !reportData.dailyData || reportData.dailyData.length === 0 ? (
          <Alert severity="info" sx={{ bgcolor: 'rgb(10, 25, 41)', color: '#B4C4A8', border: '1px solid #D4AF37' }}>
            No attendance data available for {monthNames[selectedMonth]} {selectedYear}.
          </Alert>
        ) : (
          <>
            <Card sx={{ mb: 3, bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
                  Summary - {monthNames[selectedMonth]} {selectedYear}
                </Typography>
                <Grid container spacing={2}>
                  {reportData.summary && reportData.summary[0] && (
                    <>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                          <Typography variant="h5" sx={{ color: '#81C784', fontWeight: 700 }}>
                            {reportData.summary[0].daysPresent || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Present</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                          <Typography variant="h5" sx={{ color: '#FFB74D', fontWeight: 700 }}>
                            {reportData.summary[0].daysLate || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Late</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                          <Typography variant="h5" sx={{ color: '#EF9A9A', fontWeight: 700 }}>
                            {reportData.summary[0].daysAbsent || 0}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Absent</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'rgba(33, 51, 28, 0.8)', borderRadius: 2 }}>
                          <Typography variant="h5" sx={{ color: '#D4AF37', fontWeight: 700 }}>
                            {reportData.summary[0].totalWorkingHours || '0h 0m'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Total Hours</Typography>
                        </Box>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>

            <TableContainer component={Paper} sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2A3E24' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Day</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Login</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Logout</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Break</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Working</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.dailyData
                    .filter(day => shouldShowDay(day.date))
                    .map((day) => {
                      const empRecord = day.records && day.records.find(r => r.employee_id === user?.id);
                      const leave = day.leaveEmployees && day.leaveEmployees.find(l => l.employee_id === user?.id);
                      const isHoliday = day.holidayInfo;
                      const status = getStatusDisplay(empRecord, leave, isHoliday);
                      
                      return (
                        <TableRow key={day.date} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                          <TableCell sx={{ color: '#FFFFFF' }}>
                            {user?.first_name} {user?.last_name}
                            <Typography variant="caption" sx={{ display: 'block', color: '#B4C4A8' }}>
                              {user?.employee_id}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: '#B4C4A8' }}>{user?.department || '-'}</TableCell>
                          <TableCell sx={{ color: '#FFFFFF' }}>
                            {new Date(day.date).getDate()}
                          </TableCell>
                          <TableCell sx={{ color: '#B4C4A8' }}>
                            {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={status.icon}
                              label={status.label} 
                              size="small"
                              sx={{ 
                                bgcolor: `${status.color}20`, 
                                color: status.color,
                                '& .MuiChip-icon': { color: status.color }
                              }} 
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#FFFFFF' }}>
                            {empRecord?.login_time ? formatTime(empRecord.login_time) : '-'}
                          </TableCell>
                          <TableCell sx={{ color: '#FFFFFF' }}>
                            {empRecord?.logout_time ? formatTime(empRecord.logout_time) : '-'}
                          </TableCell>
                          <TableCell sx={{ color: '#F57C00' }}>
                            {empRecord?.total_break_time ? formatMinutes(empRecord.total_break_time) : '-'}
                          </TableCell>
                          <TableCell sx={{ color: '#D4AF37' }}>
                            {empRecord?.net_working_time ? formatMinutes(empRecord.net_working_time) : '-'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small" 
                                onClick={() => {
                                  setSelectedAttendance(empRecord);
                                  setShowDetailDialog(true);
                                }}
                                sx={{ color: '#D4AF37' }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#B4C4A8' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 2 }}>
                <CheckCircle sx={{ fontSize: 14, color: '#81C784', mr: 0.5 }} /> Present
              </Box>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 2 }}>
                <Schedule sx={{ fontSize: 14, color: '#FFB74D', mr: 0.5 }} /> Late
              </Box>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 2 }}>
                <Cancel sx={{ fontSize: 14, color: '#EF9A9A', mr: 0.5 }} /> Absent
              </Box>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 2 }}>
                <EventBusy sx={{ fontSize: 14, color: '#90CAF9', mr: 0.5 }} /> Leave
              </Box>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 2 }}>
                <HolidayVillage sx={{ fontSize: 14, color: '#CE93D8', mr: 0.5 }} /> Holiday
              </Box>
            </Typography>
          </>
        )}

        <Dialog 
          open={showDetailDialog} 
          onClose={() => setShowDetailDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37' }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37' }}>Attendance Details</DialogTitle>
          <DialogContent>
            {selectedAttendance ? (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Date</Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {new Date(selectedAttendance.date).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Status</Typography>
                  <Typography variant="body1" sx={{ color: selectedAttendance.status === 'late' ? '#FFB74D' : '#81C784' }}>
                    {selectedAttendance.status === 'late' ? 'Late' : 'Present'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Login Time</Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {formatTime(selectedAttendance.login_time)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Logout Time</Typography>
                  <Typography variant="body1" sx={{ color: '#FFFFFF' }}>
                    {formatTime(selectedAttendance.logout_time)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Break Time</Typography>
                  <Typography variant="body1" sx={{ color: '#F57C00' }}>
                    {selectedAttendance.total_break_time ? formatMinutes(selectedAttendance.total_break_time) : '0h 0m'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ color: '#B4C4A8' }}>Net Working Time</Typography>
                  <Typography variant="body1" sx={{ color: '#D4AF37' }}>
                    {selectedAttendance.net_working_time ? formatMinutes(selectedAttendance.net_working_time) : '0h 0m'}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography sx={{ color: '#B4C4A8' }}>No attendance record found</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDetailDialog(false)} sx={{ color: '#D4AF37' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default EmployeeReports;